import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { ProductRecognitionDetectionContent } from '../../../pages/store-details/components/ProductRecognitionDetectionContent';
import type {
  ProductRecognitionBBox,
  ProductRecognitionPolygon,
  VizSimCandidate,
  VizSimReferenceCrop,
} from '../../../types';
import { MetricsSummary } from '../../components/common/MetricsSummary';
import { RunTypeBadge } from '../../components/common/RunTypeBadge';
import { EvalApiService } from '../../services/EvalApiService';
import type { PogItemSnapshot } from '../../types';
import { EvalGtSample, EvalRun, RunInferenceDetail } from '../../types';
import { fmtIso } from '../../utils';

// ── Layer definitions ─────────────────────────────────────────────────────────

interface LayerDef {
  key: string;
  label: string;
  fill: string;
  stroke: string;
  type: 'polygon' | 'bbox';
}

const LAYERS: LayerDef[] = [
  {
    key: 'product_polygons',
    label: 'Products',
    fill: 'rgba(56,189,248,0.18)',
    stroke: 'rgba(56,189,248,0.95)',
    type: 'polygon',
  },
  {
    key: 'carton_polygons',
    label: 'Cartons',
    fill: 'rgba(251,146,60,0.18)',
    stroke: 'rgba(251,146,60,0.95)',
    type: 'polygon',
  },
  {
    key: 'mod_polygons',
    label: 'Mods',
    fill: 'rgba(74,222,128,0.18)',
    stroke: 'rgba(74,222,128,0.95)',
    type: 'polygon',
  },
  {
    key: 'shelf_boxes',
    label: 'Shelves',
    fill: 'rgba(250,204,21,0.18)',
    stroke: 'rgba(250,204,21,0.95)',
    type: 'bbox',
  },
  {
    key: 'template_boxes',
    label: 'Template Boxes',
    fill: 'rgba(192,132,252,0.18)',
    stroke: 'rgba(192,132,252,0.95)',
    type: 'bbox',
  },
  {
    key: 'price_tag_boxes',
    label: 'Price Tags',
    fill: 'rgba(248,113,113,0.18)',
    stroke: 'rgba(248,113,113,0.95)',
    type: 'bbox',
  },
];

// PR runs only show products and cartons, coloured by barcode agreement.
const PR_LAYERS = LAYERS.filter((l) => l.key === 'product_polygons' || l.key === 'carton_polygons');

// template_box_shape_type tells us which of the two template box lists is active.
// 'template_box_up' → template_boxes_up; anything else → template_boxes.
function resolveLayerKey(key: string, inference: Record<string, unknown> | null): string {
  if (key === 'template_boxes') {
    const shapeType = inference?.template_box_shape_type as string | undefined;
    return shapeType === 'template_box_up' ? 'template_boxes_up' : 'template_boxes';
  }
  return key;
}

function shapeCount(inference: Record<string, unknown> | null, key: string): number {
  if (!inference) return 0;
  const arr = inference[resolveLayerKey(key, inference)];
  return Array.isArray(arr) ? arr.length : 0;
}

// ── Canvas drawing ────────────────────────────────────────────────────────────

const TP_COLORS = { fill: 'rgba(59,130,246,0.15)', stroke: 'rgba(59,130,246,0.9)' };
const FP_COLORS = { fill: 'rgba(234,179,8,0.15)', stroke: 'rgba(234,179,8,0.9)' };
const FN_COLORS = { fill: 'rgba(239,68,68,0.15)', stroke: 'rgba(239,68,68,0.9)' };
const CORRECT_COLORS = { fill: 'rgba(59,130,246,0.15)', stroke: 'rgba(59,130,246,0.9)' };
const WRONG_COLORS = { fill: 'rgba(239,68,68,0.15)', stroke: 'rgba(239,68,68,0.9)' };

interface PcHighlight {
  gtUuids: Set<string>;
  runUuids: Set<string>;
  gtShapes: Record<string, unknown>[];
  runShapes: Record<string, unknown>[];
}

function getBarcode(shape: Record<string, unknown>): string | null {
  const cands = shape.viz_sim_candidates as Array<Record<string, unknown>> | undefined;
  return (cands?.[0]?.normalized_barcode as string) ?? null;
}

function paintShape(
  ctx: CanvasRenderingContext2D,
  s: Record<string, unknown>,
  layer: LayerDef,
  fill: string,
  stroke: string,
  lineWidth: number
) {
  ctx.lineWidth = lineWidth;
  ctx.fillStyle = fill;
  ctx.strokeStyle = stroke;
  if (layer.type === 'polygon') {
    const pts = s.points as Array<{ x: number; y: number }> | undefined;
    if (!pts?.length) return;
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  } else {
    const x1 = s.x1 as number,
      y1 = s.y1 as number;
    const x2 = s.x2 as number,
      y2 = s.y2 as number;
    ctx.beginPath();
    ctx.rect(x1, y1, x2 - x1, y2 - y1);
    ctx.fill();
    ctx.stroke();
  }
}

function drawLegend(
  ctx: CanvasRenderingContext2D,
  lw: number,
  items: Array<{ color: string; label: string }>
) {
  const fs = Math.round(lw * 1.7);
  const pad = Math.round(lw * 0.9);
  const sw = Math.round(lw * 1.4);
  const rowH = fs + pad;

  const boxW = fs * 11 + pad * 3 + sw;
  const boxH = items.length * rowH + pad * 2;

  ctx.fillStyle = 'rgba(0,0,0,0.62)';
  ctx.beginPath();
  ctx.rect(0, 0, boxW, boxH);
  ctx.fill();

  ctx.font = `600 ${fs}px sans-serif`;
  ctx.textBaseline = 'middle';

  for (let i = 0; i < items.length; i++) {
    const ry = pad + i * rowH + rowH / 2;
    ctx.fillStyle = items[i].color;
    ctx.fillRect(pad, ry - sw / 2, sw, sw);
    ctx.fillStyle = '#fff';
    ctx.fillText(items[i].label, pad + sw + pad, ry);
  }
}

const OD_LEGEND = [
  { color: TP_COLORS.stroke, label: 'TP  True Positive' },
  { color: FP_COLORS.stroke, label: 'FP  False Positive' },
  { color: FN_COLORS.stroke, label: 'FN  False Negative' },
];

const PR_LEGEND = [
  { color: CORRECT_COLORS.stroke, label: 'Correct barcode' },
  { color: WRONG_COLORS.stroke, label: 'Incorrect barcode' },
];

function drawShapes(
  canvas: HTMLCanvasElement,
  prInference: Record<string, unknown> | null,
  gtInference: Record<string, unknown> | null,
  assignment: Record<string, Record<string, string>> | null,
  visible: Set<string>,
  lineWidth: number,
  runType: string | null,
  pcHighlight?: PcHighlight | null
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  if (runType === 'PC' || runType === 'Full') {
    if (!pcHighlight) return;
    const GT_FILL = 'rgba(34,197,94,0.6)';
    const RUN_STROKE = 'rgba(59,130,246,0.9)';
    for (const shape of pcHighlight.gtShapes) {
      const uuid = shape.uuid as string | undefined;
      if (!uuid || !pcHighlight.gtUuids.has(uuid)) continue;
      paintShape(ctx, shape, LAYERS[0], GT_FILL, 'rgba(0,0,0,0)', lineWidth);
    }
    for (const shape of pcHighlight.runShapes) {
      const uuid = shape.uuid as string | undefined;
      if (!uuid || !pcHighlight.runUuids.has(uuid)) continue;
      paintShape(ctx, shape, LAYERS[0], 'rgba(0,0,0,0)', RUN_STROKE, lineWidth * 1.5);
    }
    return;
  }

  if (runType === 'PR') {
    // PR mode: only assigned products/cartons, coloured by barcode agreement.
    for (const layer of PR_LAYERS) {
      if (!visible.has(layer.key)) continue;
      const resolvedKey = resolveLayerKey(layer.key, gtInference);
      const prShapes = (prInference?.[resolvedKey] as unknown[]) ?? [];
      const gtShapes = (gtInference?.[resolvedKey] as unknown[]) ?? [];

      if (!assignment) continue;

      const classMap = assignment[resolvedKey] ?? {};

      // Build uuid → shape map for GT so we can look up the matched GT shape
      const gtByUuid = new Map<string, Record<string, unknown>>();
      for (const shape of gtShapes) {
        const s = shape as Record<string, unknown>;
        const uuid = s.uuid as string | undefined;
        if (uuid) gtByUuid.set(uuid, s);
      }

      for (const shape of prShapes) {
        const s = shape as Record<string, unknown>;
        const prUuid = s.uuid as string | undefined;
        if (prUuid == null || !(prUuid in classMap)) continue; // unassigned — skip

        const gtShape = gtByUuid.get(classMap[prUuid]);
        const prBarcode = getBarcode(s);
        const gtBarcode = gtShape ? getBarcode(gtShape) : null;
        const correct = prBarcode != null && gtBarcode != null && prBarcode === gtBarcode;
        const c = correct ? CORRECT_COLORS : WRONG_COLORS;
        paintShape(ctx, s, layer, c.fill, c.stroke, lineWidth);
      }
    }
    drawLegend(ctx, lineWidth, PR_LEGEND);
    return;
  }

  // OD mode (default)
  for (const layer of LAYERS) {
    if (!visible.has(layer.key)) continue;

    // GT governs which actual array key to use (template_boxes vs template_boxes_up).
    // The same resolved key is used for PR shapes, GT shapes, and the assignment lookup
    // so that all three are in sync.
    const resolvedKey = resolveLayerKey(layer.key, gtInference);
    const prShapes = (prInference?.[resolvedKey] as unknown[]) ?? [];
    const gtShapes = (gtInference?.[resolvedKey] as unknown[]) ?? [];

    if (!assignment) {
      // No assignment data — draw all PR shapes in layer colour
      for (const shape of prShapes) {
        paintShape(
          ctx,
          shape as Record<string, unknown>,
          layer,
          layer.fill,
          layer.stroke,
          lineWidth
        );
      }
      continue;
    }

    const classMap = assignment[resolvedKey] ?? {};
    const assignedGtUuids = new Set(Object.values(classMap));

    // TP / FP from PR shapes
    for (const shape of prShapes) {
      const s = shape as Record<string, unknown>;
      const uuid = s.uuid as string | undefined;
      const isTP = uuid != null && uuid in classMap;
      const c = isTP ? TP_COLORS : FP_COLORS;
      paintShape(ctx, s, layer, c.fill, c.stroke, lineWidth);
    }

    // FN from GT shapes not covered by any assignment
    for (const shape of gtShapes) {
      const s = shape as Record<string, unknown>;
      const uuid = s.uuid as string | undefined;
      if (uuid == null || assignedGtUuids.has(uuid)) continue;
      paintShape(ctx, s, layer, FN_COLORS.fill, FN_COLORS.stroke, lineWidth);
    }
  }

  if (assignment) drawLegend(ctx, lineWidth, OD_LEGEND);
}

// ── Hit testing ───────────────────────────────────────────────────────────────

function pointInPolygon(x: number, y: number, pts: Array<{ x: number; y: number }>): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x,
      yi = pts[i].y;
    const xj = pts[j].x,
      yj = pts[j].y;
    // prettier-ignore
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function pointInBBox(x: number, y: number, s: Record<string, unknown>): boolean {
  return (
    x >= (s.x1 as number) && x <= (s.x2 as number) && y >= (s.y1 as number) && y <= (s.y2 as number)
  );
}

// ── Raw shape adaptation ──────────────────────────────────────────────────────

function adaptVizSimCandidate(raw: Record<string, unknown>): VizSimCandidate {
  return {
    normalizedBarcode: (raw.normalized_barcode as string) ?? '',
    similarity: raw.similarity as number | null,
    isConfident: (raw.is_confident as boolean) ?? false,
    productName: raw.product_name as string | undefined,
    productImageUri: raw.product_image_uri as string | undefined,
    itemNumber: (raw.item_number as string | null | undefined) ?? undefined,
    productId: (raw.product_id as number | null | undefined) ?? undefined,
    referenceCrops: (raw.reference_crops as Array<Record<string, unknown>> | undefined)?.map(
      (rc) => ({
        embeddingCropId: rc.embedding_crop_id as string,
        similarity: rc.similarity as number,
      })
    ) as VizSimReferenceCrop[] | undefined,
    allNormalizedBarcodes: raw.all_normalized_barcodes as string[] | undefined,
    isActive: true,
  };
}

function adaptRawShape(
  raw: Record<string, unknown>
): ProductRecognitionPolygon | ProductRecognitionBBox {
  const vizSimCandidates = (
    raw.viz_sim_candidates as Array<Record<string, unknown>> | undefined
  )?.map(adaptVizSimCandidate);
  const base = { confidence: raw.confidence as number | undefined, vizSimCandidates };
  if (Array.isArray(raw.points) && raw.points.length > 0) {
    const points = raw.points as Array<{ x: number; y: number }>;
    const xs = points.map((p) => p.x);
    const ys = points.map((p) => p.y);
    const minX = Math.min(...xs),
      minY = Math.min(...ys);
    const maxX = Math.max(...xs),
      maxY = Math.max(...ys);
    return {
      ...base,
      points,
      shape: [minX, minY, maxX - minX, maxY - minY] as [number, number, number, number],
    } as ProductRecognitionPolygon;
  }
  return {
    ...base,
    x1: raw.x1 as number,
    y1: raw.y1 as number,
    x2: raw.x2 as number,
    y2: raw.y2 as number,
  } as ProductRecognitionBBox;
}

// ── Image viewer with zoom/pan ────────────────────────────────────────────────

const ICON_BTN: React.CSSProperties = {
  background: 'rgba(0,0,0,0.55)',
  border: '1px solid rgba(255,255,255,0.15)',
  color: '#fff',
  borderRadius: 6,
  padding: '4px 8px',
  fontSize: 12,
  cursor: 'pointer',
  lineHeight: 1,
  backdropFilter: 'blur(4px)',
  userSelect: 'none',
};

interface ImageViewerProps {
  url: string | null | undefined;
  prInference: Record<string, unknown> | null;
  gtInference: Record<string, unknown> | null;
  assignment: Record<string, Record<string, string>> | null;
  visible: Set<string>;
  runType: string | null;
  pcHighlight?: PcHighlight | null;
  maximized?: boolean;
  onMaximize?: () => void;
  onClose?: () => void;
  onShapeRightClick?: (
    prShape: Record<string, unknown>,
    gtShape: Record<string, unknown> | null,
    layerKey: string,
    screenX: number,
    screenY: number
  ) => void;
}

function ImageViewer({
  url,
  prInference,
  gtInference,
  assignment,
  visible,
  runType,
  pcHighlight,
  maximized = false,
  onMaximize,
  onClose,
  onShapeRightClick,
}: ImageViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Zoom/pan state — keep in refs to avoid stale closures in event handlers
  const scaleRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });
  const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  scaleRef.current = transform.scale;
  offsetRef.current = { x: transform.x, y: transform.y };

  const panRef = useRef<{ startX: number; startY: number; ox: number; oy: number } | null>(null);
  const [panning, setPanning] = useState(false);

  // Reset transform when url changes or maximized toggles
  useEffect(() => {
    setTransform({ scale: 1, x: 0, y: 0 });
  }, [url, maximized]);

  // Canvas: set natural dimensions on image load
  const syncCanvas = useCallback(() => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas || !img.naturalWidth) return;
    if (canvas.width !== img.naturalWidth || canvas.height !== img.naturalHeight) {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
    }
    // Scale lineWidth so it appears ~3px visually regardless of image resolution.
    // ratio = natural px per display px; e.g. 4000px image at 400px display → ratio=10 → lineWidth=30 → 3px on screen.
    const ratio = img.clientWidth > 0 ? img.naturalWidth / img.clientWidth : 1;
    drawShapes(
      canvas,
      prInference,
      gtInference,
      assignment,
      visible,
      Math.max(1, 3 * ratio),
      runType,
      pcHighlight
    );
  }, [prInference, gtInference, assignment, visible, runType, pcHighlight]);

  // Redraw every render — cheap clearRect + draw
  useEffect(() => {
    syncCanvas();
  });

  // Wheel zoom — passive: false so we can preventDefault
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    const prev = scaleRef.current;
    const next = Math.max(0.25, Math.min(20, prev * factor));
    const ox = offsetRef.current.x;
    const oy = offsetRef.current.y;
    setTransform({
      scale: next,
      x: mx - (mx - ox) * (next / prev),
      y: my - (my - oy) * (next / prev),
    });
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // Drag to pan
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    panRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      ox: offsetRef.current.x,
      oy: offsetRef.current.y,
    };
    setPanning(true);
  };

  useEffect(() => {
    if (!panning) return;
    const onMove = (e: MouseEvent) => {
      const pan = panRef.current;
      if (!pan) return;
      setTransform((prev) => ({
        ...prev,
        x: pan.ox + e.clientX - pan.startX,
        y: pan.oy + e.clientY - pan.startY,
      }));
    };
    const onUp = () => {
      panRef.current = null;
      setPanning(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [panning]);

  const resetZoom = (e: React.MouseEvent) => {
    e.stopPropagation();
    setTransform({ scale: 1, x: 0, y: 0 });
  };

  // Right-click: hit-test visible PR shapes and fire callback
  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (!onShapeRightClick || runType !== 'PR') return;
      e.preventDefault();
      const img = imgRef.current;
      const container = containerRef.current;
      if (!img || !container || !img.naturalWidth) return;
      const rect = container.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      // Undo CSS transform → display coords → natural image pixels
      const ix = (cx - offsetRef.current.x) / scaleRef.current;
      const iy = (cy - offsetRef.current.y) / scaleRef.current;
      const nx = ix * (img.naturalWidth / img.clientWidth);
      const ny = iy * (img.naturalHeight / img.clientHeight);
      if (nx < 0 || ny < 0 || nx > img.naturalWidth || ny > img.naturalHeight) return;
      for (const layer of PR_LAYERS) {
        if (!visible.has(layer.key)) continue;
        const resolvedKey = resolveLayerKey(layer.key, gtInference);
        const prShapes = (prInference?.[resolvedKey] as unknown[]) ?? [];
        for (const shape of prShapes) {
          const s = shape as Record<string, unknown>;
          const pts = s.points as Array<{ x: number; y: number }> | undefined;
          const hit =
            layer.type === 'polygon'
              ? pts?.length
                ? pointInPolygon(nx, ny, pts)
                : false
              : pointInBBox(nx, ny, s);
          if (!hit) continue;
          const classMap = (assignment ?? {})[resolvedKey] ?? {};
          const prUuid = s.uuid as string | undefined;
          let gtShape: Record<string, unknown> | null = null;
          if (prUuid && prUuid in classMap) {
            const gtShapes = (gtInference?.[resolvedKey] as unknown[]) ?? [];
            const found = gtShapes.find(
              (g) => (g as Record<string, unknown>).uuid === classMap[prUuid]
            );
            if (found) gtShape = found as Record<string, unknown>;
          }
          onShapeRightClick(s, gtShape, resolvedKey, e.clientX, e.clientY);
          return;
        }
      }
    },
    [onShapeRightClick, runType, visible, prInference, gtInference, assignment]
  );

  const isTransformed = transform.scale !== 1 || transform.x !== 0 || transform.y !== 0;

  const noImage = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 260,
        color: 'var(--text-secondary)',
        fontSize: 12,
      }}
    >
      No image available
    </div>
  );

  return (
    <div
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onDoubleClick={resetZoom}
      onContextMenu={handleContextMenu}
      style={{
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid var(--border)',
        borderRadius: 8,
        background: 'var(--surface-raised)',
        cursor: panning ? 'grabbing' : transform.scale > 1 ? 'grab' : 'default',
        userSelect: 'none',
        height: maximized ? 'calc(100vh - 180px)' : undefined,
        minHeight: 260,
      }}
    >
      {!url ? (
        noImage
      ) : (
        <div
          style={{
            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
            transformOrigin: '0 0',
            width: '100%',
          }}
        >
          <img
            ref={imgRef}
            src={url}
            alt="Sample"
            onLoad={syncCanvas}
            draggable={false}
            style={{ width: '100%', height: 'auto', display: 'block', pointerEvents: 'none' }}
          />
          <canvas
            ref={canvasRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          />
        </div>
      )}

      {/* Overlay controls — top-right corner */}
      <div
        style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6, zIndex: 10 }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {isTransformed && (
          <button style={ICON_BTN} onClick={resetZoom} title="Reset zoom (double-click)">
            1:1
          </button>
        )}
        {onMaximize && (
          <button
            style={ICON_BTN}
            onClick={(e) => {
              e.stopPropagation();
              onMaximize();
            }}
            title="Maximize"
          >
            ⛶
          </button>
        )}
        {onClose && (
          <button
            style={ICON_BTN}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            title="Close"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

// ── Layer toggle pills ────────────────────────────────────────────────────────

interface LayerControlsProps {
  layers: LayerDef[];
  prInference: Record<string, unknown> | null;
  visible: Set<string>;
  onChange: (key: string, checked: boolean) => void;
}

function LayerControls({ layers, prInference, visible, onChange }: LayerControlsProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        marginTop: 8,
        padding: '8px 10px',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 8,
      }}
    >
      {layers.map((layer) => {
        const count = shapeCount(prInference, layer.key);
        const active = visible.has(layer.key) && count > 0;
        const disabled = count === 0;
        return (
          <button
            key={layer.key}
            disabled={disabled}
            onClick={() => !disabled && onChange(layer.key, !visible.has(layer.key))}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '3px 10px 3px 7px',
              borderRadius: 100,
              border: `1px solid ${active ? layer.stroke : 'var(--border)'}`,
              background: active ? layer.fill : 'transparent',
              color: active ? layer.stroke : 'var(--text-tertiary)',
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled ? 0.3 : 1,
              fontSize: 11,
              fontWeight: active ? 600 : 400,
              fontFamily: 'var(--font-body)',
              transition: 'all 120ms ease',
              userSelect: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 2,
                flexShrink: 0,
                background: active ? layer.stroke : 'var(--border)',
                transition: 'background 120ms ease',
              }}
            />
            {layer.label}
            {count > 0 && (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  opacity: 0.65,
                  marginLeft: 1,
                }}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Maximize overlay ──────────────────────────────────────────────────────────

interface MaximizedOverlayProps {
  url: string | null | undefined;
  prInference: Record<string, unknown> | null;
  gtInference: Record<string, unknown> | null;
  assignment: Record<string, Record<string, string>> | null;
  visible: Set<string>;
  runType: string | null;
  pcHighlight?: PcHighlight | null;
  layers: LayerDef[];
  onToggle: (key: string, checked: boolean) => void;
  onClose: () => void;
  onShapeRightClick?: ImageViewerProps['onShapeRightClick'];
}

function MaximizedOverlay({
  url,
  prInference,
  gtInference,
  assignment,
  visible,
  runType,
  pcHighlight,
  layers,
  onToggle,
  onClose,
  onShapeRightClick,
}: MaximizedOverlayProps) {
  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.88)',
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
        gap: 10,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <ImageViewer
        url={url}
        prInference={prInference}
        gtInference={gtInference}
        assignment={assignment}
        visible={visible}
        runType={runType}
        pcHighlight={pcHighlight}
        maximized
        onClose={onClose}
        onShapeRightClick={onShapeRightClick}
      />
      {runType !== 'PC' && runType !== 'Full' && (
        <div style={{ flexShrink: 0 }}>
          <LayerControls
            layers={layers}
            prInference={prInference}
            visible={visible}
            onChange={onToggle}
          />
        </div>
      )}
    </div>
  );
}

// ── PR comparison panel ───────────────────────────────────────────────────────

interface RightClickState {
  prShape: Record<string, unknown>;
  gtShape: Record<string, unknown> | null;
  layerKey: string;
  screenX: number;
  screenY: number;
}

interface PrComparisonPanelProps {
  state: RightClickState;
  cameraImage: HTMLImageElement | null;
  onClose: () => void;
}

function DetectionPanel({
  label,
  item,
  raw,
  cameraImage,
  color,
}: {
  label: string;
  item: ProductRecognitionPolygon | ProductRecognitionBBox | null;
  raw: Record<string, unknown> | null;
  cameraImage: HTMLImageElement | null;
  color: string;
}) {
  return (
    <div className="flex flex-col">
      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{label}</div>
      <div className="flex-1 overflow-y-auto">
        {item ? (
          <>
            <ProductRecognitionDetectionContent
              item={item}
              cameraImageElement={cameraImage ?? undefined}
              layerBboxColor={color}
              withReferenceCrops
            />
            <div className="mt-6 pt-4 border-t border-gray-200">
              <span className="text-gray-600 font-medium block mb-2 text-sm">Raw Data:</span>
              <pre className="text-xs bg-gray-50 p-3 rounded overflow-x-auto max-h-48 overflow-y-auto">
                {JSON.stringify(raw, null, 2)}
              </pre>
            </div>
          </>
        ) : (
          <div className="text-gray-400 text-sm p-4">No GT match for this shape.</div>
        )}
      </div>
    </div>
  );
}

function PrComparisonPanel({ state, cameraImage, onClose }: PrComparisonPanelProps) {
  const { prShape, gtShape } = state;
  const prItem = useMemo(() => adaptRawShape(prShape), [prShape]);
  const gtItem = useMemo(() => (gtShape ? adaptRawShape(gtShape) : null), [gtShape]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0 !mb-0"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-hidden flex flex-col max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Detection Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Side-by-side panels */}
        <div className="grid grid-cols-2 divide-x divide-gray-200 overflow-y-auto">
          <div className="p-6">
            <DetectionPanel
              label="GT"
              item={gtItem}
              raw={gtShape}
              cameraImage={cameraImage}
              color="rgba(59,130,246,0.9)"
            />
          </div>
          <div className="p-6">
            <DetectionPanel
              label="Run"
              item={prItem}
              raw={prShape}
              cameraImage={cameraImage}
              color="rgba(251,146,60,0.9)"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ROG Comparison Table (PC runs) ───────────────────────────────────────────

interface RogRowData {
  position: string;
  gt: { finalItemNumber: string | null; rogItemStatuses: string[]; uuids: string[] } | null;
  run: { finalItemNumber: string | null; rogItemStatuses: string[]; uuids: string[] } | null;
}

function coerceStatusList(raw: unknown): string[] {
  // Backend now emits a list of distinct statuses. Old MongoDB docs may still
  // store a single string — treat that as a 1-element list for backward compat.
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.map((s) => String(s));
  return [String(raw)];
}

function getRogRows(
  gtPcInference: Record<string, unknown> | null,
  runPcInference: Record<string, unknown> | null
): RogRowData[] {
  const gtRog = (gtPcInference?.rog_results_by_position ?? {}) as Record<
    string,
    Record<string, unknown>
  >;
  const runRog = (runPcInference?.rog_results_by_position ?? {}) as Record<
    string,
    Record<string, unknown>
  >;
  const positions = Array.from(new Set([...Object.keys(gtRog), ...Object.keys(runRog)])).sort();
  return positions.map((pos) => ({
    position: pos,
    gt: gtRog[pos]
      ? {
          finalItemNumber: (gtRog[pos].final_item_number as string | null) ?? null,
          rogItemStatuses: coerceStatusList(gtRog[pos].rog_item_status),
          uuids: (gtRog[pos].list_of_pr_uuids as string[]) ?? [],
        }
      : null,
    run: runRog[pos]
      ? {
          finalItemNumber: (runRog[pos].final_item_number as string | null) ?? null,
          rogItemStatuses: coerceStatusList(runRog[pos].rog_item_status),
          uuids: (runRog[pos].list_of_pr_uuids as string[]) ?? [],
        }
      : null,
  }));
}

const ROG_TH: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 10px',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-tertiary)',
  borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap',
  background: 'var(--surface-raised)',
};

const ROG_TD: React.CSSProperties = {
  padding: '5px 10px',
  fontSize: 12,
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'middle',
};

const ROG_STATUS_PALETTE: Record<string, { bg: string; text: string; border: string }> = {
  correct: { bg: 'rgba(34,197,94,0.15)', text: '#16a34a', border: 'rgba(34,197,94,0.35)' },
  move: { bg: 'rgba(59,130,246,0.15)', text: '#2563eb', border: 'rgba(59,130,246,0.35)' },
  out: { bg: 'rgba(251,146,60,0.15)', text: '#d97706', border: 'rgba(251,146,60,0.35)' },
  remove: { bg: 'rgba(239,68,68,0.15)', text: '#dc2626', border: 'rgba(239,68,68,0.35)' },
  // PC compliance statuses (used by other call sites that still pass single values)
  compliant: { bg: 'rgba(34,197,94,0.15)', text: '#16a34a', border: 'rgba(34,197,94,0.35)' },
  non_compliant: { bg: 'rgba(239,68,68,0.15)', text: '#dc2626', border: 'rgba(239,68,68,0.35)' },
  faceover: { bg: 'rgba(192,132,252,0.15)', text: '#7c3aed', border: 'rgba(192,132,252,0.35)' },
};

const ROG_STATUS_FALLBACK = {
  bg: 'var(--surface-raised)',
  text: 'var(--text-secondary)',
  border: 'var(--border)',
};

function RogStatusBadges({
  statuses,
  matchedAgainst,
}: {
  statuses: string[];
  matchedAgainst?: string[]; // when provided, statuses also in this list get a check, others get an x
}) {
  if (!statuses.length) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
  const matchSet = matchedAgainst ? new Set(matchedAgainst) : null;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 3 }}>
      {statuses.map((status) => {
        const c = ROG_STATUS_PALETTE[status] ?? ROG_STATUS_FALLBACK;
        const matched = matchSet ? matchSet.has(status) : null;
        return (
          <span
            key={status}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              padding: '1px 6px',
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 600,
              fontFamily: 'var(--font-mono)',
              background: c.bg,
              color: c.text,
              border: `1px solid ${c.border}`,
              opacity: matched === false ? 0.55 : 1,
            }}
            title={
              matched === null
                ? undefined
                : matched
                  ? 'Also in the other side'
                  : 'Not in the other side'
            }
          >
            {status}
            {matched === false && <span style={{ fontSize: 9, color: 'var(--danger)' }}>•</span>}
          </span>
        );
      })}
    </span>
  );
}

interface RogTableProps {
  rows: RogRowData[];
  hoveredPosition: string | null;
  onHover: (pos: string | null) => void;
}

function RogTable({ rows, hoveredPosition, onHover }: RogTableProps) {
  if (rows.length === 0) {
    return (
      <div style={{ color: 'var(--text-tertiary)', fontSize: 12, fontStyle: 'italic' }}>
        No ROG position data available.
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-mono)' }}>
        <thead>
          <tr>
            <th style={ROG_TH}>Position</th>
            <th style={ROG_TH}>GT Item #</th>
            <th style={ROG_TH}>Run Item #</th>
            <th style={ROG_TH}>GT Status</th>
            <th style={ROG_TH}>Run Status</th>
            <th style={{ ...ROG_TH, textAlign: 'center' }}>Objects</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isHovered = hoveredPosition === row.position;
            const gtNum = row.gt?.finalItemNumber ?? null;
            const runNum = row.run?.finalItemNumber ?? null;
            const numMatch = gtNum != null && runNum != null && gtNum === runNum;
            const numMismatch = gtNum != null && runNum != null && gtNum !== runNum;
            return (
              <tr
                key={row.position}
                onMouseEnter={() => onHover(row.position)}
                onMouseLeave={() => onHover(null)}
                style={{
                  background: isHovered ? 'rgba(59,130,246,0.06)' : undefined,
                  cursor: 'default',
                  transition: 'background 80ms ease',
                }}
              >
                <td style={{ ...ROG_TD, fontWeight: 600, color: 'var(--text-primary)' }}>
                  {row.position}
                </td>
                <td
                  style={{
                    ...ROG_TD,
                    color: numMatch
                      ? 'var(--positive)'
                      : numMismatch
                        ? 'var(--danger)'
                        : 'var(--text-secondary)',
                  }}
                >
                  {gtNum ?? '—'}
                </td>
                <td
                  style={{
                    ...ROG_TD,
                    color: numMatch
                      ? 'var(--positive)'
                      : numMismatch
                        ? 'var(--danger)'
                        : 'var(--text-secondary)',
                  }}
                >
                  {runNum ?? '—'}
                </td>
                <td style={ROG_TD}>
                  <RogStatusBadges
                    statuses={row.gt?.rogItemStatuses ?? []}
                    matchedAgainst={row.run?.rogItemStatuses}
                  />
                </td>
                <td style={ROG_TD}>
                  <RogStatusBadges
                    statuses={row.run?.rogItemStatuses ?? []}
                    matchedAgainst={row.gt?.rogItemStatuses}
                  />
                </td>
                <td
                  style={{
                    ...ROG_TD,
                    textAlign: 'center',
                    color:
                      (row.gt?.uuids.length ?? 0) === 0 && (row.run?.uuids.length ?? 0) === 0
                        ? 'var(--text-tertiary)'
                        : 'var(--text-secondary)',
                  }}
                >
                  <span title="GT linked objects">{row.gt?.uuids.length ?? 0}</span>
                  <span style={{ color: 'var(--text-tertiary)', margin: '0 3px' }}>/</span>
                  <span title="Run linked objects">{row.run?.uuids.length ?? 0}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── ROG Planogram Grid (PC runs) ─────────────────────────────────────────────

function pogStatusAbbrev(status: string | null): string {
  if (!status) return '·';
  switch (status) {
    case 'compliant':
      return 'c';
    case 'non_compliant':
      return 'n';
    case 'out':
      return 'o';
    case 'faceover':
      return 'f';
    default:
      return status[0];
  }
}

interface PogCellTooltip {
  posKey: string;
  il_item_id: number;
  gtStatus: string | null;
  runStatus: string | null;
  rectTop: number;
  rectLeft: number;
  rectWidth: number;
}

interface RogPlanogramGridProps {
  pogItems: PogItemSnapshot[];
  gtPcInference: Record<string, unknown> | null;
  runPcInference: Record<string, unknown> | null;
  hoveredPosition: string | null;
  onHover: (pos: string | null) => void;
}

function RogPlanogramGrid({
  pogItems,
  gtPcInference,
  runPcInference,
  hoveredPosition,
  onHover,
}: RogPlanogramGridProps) {
  const [tooltip, setTooltip] = useState<PogCellTooltip | null>(null);

  const gtPogByIli = useMemo(
    () => (gtPcInference?.pog_results_by_ili ?? {}) as Record<string, string>,
    [gtPcInference]
  );
  const runPogByIli = useMemo(
    () => (runPcInference?.pog_results_by_ili ?? {}) as Record<string, string>,
    [runPcInference]
  );

  // Group pog_items: mod → shelf (desc) → sorted items
  const byMod = useMemo(() => {
    const map = new Map<number, Map<number, PogItemSnapshot[]>>();
    for (const item of pogItems) {
      if (!map.has(item.mod)) map.set(item.mod, new Map());
      const byShelf = map.get(item.mod)!;
      if (!byShelf.has(item.shelf)) byShelf.set(item.shelf, []);
      byShelf.get(item.shelf)!.push(item);
    }
    return map;
  }, [pogItems]);

  const mods = useMemo(() => Array.from(byMod.keys()).sort((a, b) => a - b), [byMod]);

  if (mods.length === 0) return null;

  return (
    <div style={{ position: 'relative' }}>
      {tooltip && (
        <div
          style={{
            position: 'fixed',
            top: tooltip.rectTop - 80,
            left: tooltip.rectLeft + tooltip.rectWidth / 2,
            transform: 'translateX(-50%)',
            background: 'var(--surface-raised)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '6px 10px',
            fontSize: 11,
            fontFamily: 'var(--font-mono)',
            zIndex: 200,
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            whiteSpace: 'nowrap',
          }}
        >
          <div style={{ color: 'var(--text-tertiary)', marginBottom: 2 }}>
            ili: {tooltip.il_item_id}
          </div>
          <div>
            <span style={{ color: 'var(--text-tertiary)' }}>GT: </span>
            <span
              style={{
                color: tooltip.gtStatus
                  ? tooltip.gtStatus === tooltip.runStatus
                    ? '#16a34a'
                    : '#dc2626'
                  : 'var(--text-tertiary)',
              }}
            >
              {tooltip.gtStatus ?? '—'}
            </span>
          </div>
          <div>
            <span style={{ color: 'var(--text-tertiary)' }}>Run: </span>
            <span
              style={{
                color: tooltip.runStatus
                  ? tooltip.gtStatus === tooltip.runStatus
                    ? '#16a34a'
                    : '#dc2626'
                  : 'var(--text-tertiary)',
              }}
            >
              {tooltip.runStatus ?? '—'}
            </span>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {mods.map((mod) => {
          const byShelf = byMod.get(mod)!;
          const shelves = Array.from(byShelf.keys()).sort((a, b) => a - b); // shelf 1 at top

          return (
            <div key={mod}>
              <div
                style={{
                  fontSize: 10,
                  color: 'var(--text-tertiary)',
                  fontFamily: 'var(--font-mono)',
                  marginBottom: 4,
                }}
              >
                MOD {mod}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {shelves.map((shelf) => {
                  const items = byShelf
                    .get(shelf)!
                    .slice()
                    .sort((a, b) => a.position - b.position);

                  return (
                    <div key={shelf} style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <div
                        style={{
                          fontSize: 9,
                          color: 'var(--text-tertiary)',
                          width: 18,
                          textAlign: 'right',
                          flexShrink: 0,
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        {shelf}
                      </div>
                      {items.map((item) => {
                        const posKey = `${item.mod},${item.shelf},${item.position}`;
                        const iliKey = String(item.il_item_id);
                        const gtStatus = gtPogByIli[iliKey] ?? null;
                        const runStatus = runPogByIli[iliKey] ?? null;

                        const hasData = gtStatus != null || runStatus != null;
                        const agree =
                          gtStatus != null && runStatus != null && gtStatus === runStatus;
                        const disagree =
                          gtStatus != null && runStatus != null && gtStatus !== runStatus;
                        const isHovered = hoveredPosition === posKey;

                        let bg = 'rgba(150,150,150,0.08)';
                        let borderColor = 'rgba(150,150,150,0.2)';
                        if (agree) {
                          bg = 'rgba(34,197,94,0.18)';
                          borderColor = 'rgba(34,197,94,0.55)';
                        } else if (disagree) {
                          bg = 'rgba(239,68,68,0.18)';
                          borderColor = 'rgba(239,68,68,0.55)';
                        } else if (hasData) {
                          bg = 'rgba(251,146,60,0.18)';
                          borderColor = 'rgba(251,146,60,0.55)';
                        }

                        return (
                          <div
                            key={posKey}
                            onMouseEnter={(e) => {
                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              setTooltip({
                                posKey,
                                il_item_id: item.il_item_id,
                                gtStatus,
                                runStatus,
                                rectTop: rect.top,
                                rectLeft: rect.left,
                                rectWidth: rect.width,
                              });
                              onHover(posKey);
                            }}
                            onMouseLeave={() => {
                              setTooltip(null);
                              onHover(null);
                            }}
                            style={{
                              width: 30,
                              height: 34,
                              border: `1px solid ${isHovered ? 'rgba(59,130,246,0.7)' : borderColor}`,
                              borderRadius: 3,
                              background: isHovered ? 'rgba(59,130,246,0.1)' : bg,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                              cursor: 'default',
                              fontFamily: 'var(--font-mono)',
                              transition: 'background 60ms ease, border-color 60ms ease',
                              flexShrink: 0,
                            }}
                          >
                            <span style={{ fontSize: 8, color: 'var(--text-tertiary)' }}>
                              {item.position}
                            </span>
                            <span
                              style={{
                                fontSize: 7,
                                color: 'var(--text-tertiary)',
                                letterSpacing: '-0.5px',
                              }}
                            >
                              {pogStatusAbbrev(gtStatus)}/{pogStatusAbbrev(runStatus)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const RunInferenceSamplePage: React.FC = () => {
  const { runId, runInferenceId } = useParams<{ runId: string; runInferenceId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuthContext();
  const [detail, setDetail] = useState<RunInferenceDetail | null>(null);
  const [run, setRun] = useState<EvalRun | null>(null);
  const [gtSample, setGtSample] = useState<EvalGtSample | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [visible, setVisible] = useState<Set<string>>(new Set());
  const [maximized, setMaximized] = useState(false);
  const [rightClick, setRightClick] = useState<RightClickState | null>(null);
  const [cameraImg, setCameraImg] = useState<HTMLImageElement | null>(null);
  const [hoveredRogPosition, setHoveredRogPosition] = useState<string | null>(null);
  const [siblings, setSiblings] = useState<
    Array<{ run_inference_id: string; gt_sample_id: string }>
  >([]);

  useEffect(() => {
    if (!runId || !runInferenceId) return;
    // Reset state on navigation so the loading screen replaces the previous sample
    // instead of showing stale content for several seconds.
    setLoading(true);
    setNotFound(false);
    setDetail(null);
    setGtSample(null);
    setCameraImg(null);
    setHoveredRogPosition(null);

    const gtId = searchParams.get('gt');
    Promise.all([
      EvalApiService.getRunInference(token ?? '', runId, runInferenceId),
      EvalApiService.getRun(token ?? '', runId),
      gtId ? EvalApiService.getGtSample(token ?? '', gtId) : Promise.resolve(null),
    ])
      .then(([d, r, gt]) => {
        if (!d) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setDetail(d);
        setRun(r);
        if (gt) {
          setGtSample(gt);
        } else {
          EvalApiService.getGtSample(token ?? '', d.gt_sample.gt_sample_id)
            .then((s) => {
              if (s) setGtSample(s);
            })
            .catch(() => {});
        }
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [runId, runInferenceId, token, searchParams]);

  // Load siblings list for prev/next nav — ids only, no inference payloads.
  useEffect(() => {
    if (!runId) return;
    EvalApiService.getRunInferences(token ?? '', runId, { include_inferences: false, limit: 500 })
      .then(({ items }) =>
        setSiblings(
          items.map((it) => ({
            run_inference_id: it.run_inference_id,
            gt_sample_id: it.gt_sample_id,
          }))
        )
      )
      .catch(() => setSiblings([]));
  }, [runId, token]);

  const siblingNav = useMemo(() => {
    if (!runInferenceId || siblings.length === 0) return { prev: null, next: null, idx: -1 };
    const idx = siblings.findIndex((s) => s.run_inference_id === runInferenceId);
    if (idx < 0) return { prev: null, next: null, idx: -1 };
    return {
      idx,
      prev: idx > 0 ? siblings[idx - 1] : null,
      next: idx < siblings.length - 1 ? siblings[idx + 1] : null,
    };
  }, [siblings, runInferenceId]);

  const goToSibling = useCallback(
    (sib: { run_inference_id: string; gt_sample_id: string } | null) => {
      if (!sib || !runId) return;
      navigate(`/eval/runs/${runId}/inferences/${sib.run_inference_id}?gt=${sib.gt_sample_id}`);
    },
    [navigate, runId]
  );

  // Compute imageUrl before hooks (detail may be null before load)
  const imageUrl = detail ? (gtSample?.image_url ?? detail.gt_sample?.image_url) : undefined;

  // Load camera image element for crop visualisation in the comparison panel
  useEffect(() => {
    if (!imageUrl) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setCameraImg(img);
    img.src = imageUrl;
  }, [imageUrl]);

  const handleShapeRightClick = useCallback(
    (
      prShape: Record<string, unknown>,
      gtShape: Record<string, unknown> | null,
      layerKey: string,
      screenX: number,
      screenY: number
    ) => {
      setRightClick({ prShape, gtShape, layerKey, screenX, screenY });
    },
    []
  );

  const pcHighlight = useMemo<PcHighlight | null>(() => {
    if ((run?.run_type !== 'PC' && run?.run_type !== 'Full') || !hoveredRogPosition || !detail)
      return null;
    const pcInf = detail.pc_inference as Record<string, unknown> | null;
    const gtPcInf = (detail.gt_sample?.pc_inference ?? null) as Record<string, unknown> | null;
    const rogByPos = (pcInf?.rog_results_by_position ?? {}) as Record<
      string,
      Record<string, unknown>
    >;
    const gtRogByPos = (gtPcInf?.rog_results_by_position ?? {}) as Record<
      string,
      Record<string, unknown>
    >;
    const runRog = rogByPos[hoveredRogPosition];
    const gtRog = gtRogByPos[hoveredRogPosition];
    const runUuids = new Set<string>((runRog?.list_of_pr_uuids as string[] | undefined) ?? []);
    const gtUuids = new Set<string>((gtRog?.list_of_pr_uuids as string[] | undefined) ?? []);
    const gtPrInf = (detail.gt_sample?.pr_inference ?? null) as Record<string, unknown> | null;
    const runPrInf = detail.pr_inference;
    const gtShapes = [
      ...((gtPrInf?.product_polygons as Record<string, unknown>[] | undefined) ?? []),
      ...((gtPrInf?.carton_polygons as Record<string, unknown>[] | undefined) ?? []),
    ];
    const runShapes = [
      ...((runPrInf?.product_polygons as Record<string, unknown>[] | undefined) ?? []),
      ...((runPrInf?.carton_polygons as Record<string, unknown>[] | undefined) ?? []),
    ];
    return { gtUuids, runUuids, gtShapes, runShapes };
  }, [run?.run_type, hoveredRogPosition, detail]);

  const rogRows = useMemo<RogRowData[]>(() => {
    if ((run?.run_type !== 'PC' && run?.run_type !== 'Full') || !detail) return [];
    const pcInf = detail.pc_inference as Record<string, unknown> | null;
    const gtPcInf = (detail.gt_sample?.pc_inference ?? null) as Record<string, unknown> | null;
    return getRogRows(gtPcInf, pcInf);
  }, [run?.run_type, detail]);

  if (loading)
    return (
      <div className="page-content">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
            minHeight: 360,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              border: '3px solid var(--border)',
              borderTopColor: 'var(--accent)',
              borderRadius: '50%',
              animation: 'eval-spin 0.8s linear infinite',
            }}
          />
          <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading sample…</div>
        </div>
        <style>{`@keyframes eval-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  if (notFound || !detail)
    return (
      <div className="page-content">
        <div style={{ color: 'var(--danger)', fontSize: 13 }}>Sample not found.</div>
      </div>
    );

  const prInference = detail.pr_inference as Record<string, unknown> | null;
  const gtInferencePr = (gtSample?.pr_inference ?? detail.gt_sample?.pr_inference) as Record<
    string,
    unknown
  > | null;
  const assignment = detail.run_to_gt_assignment;
  const runType = run?.run_type ?? null;
  const isPcLike = runType === 'PC' || runType === 'Full';
  const layers = runType === 'PR' ? PR_LAYERS : LAYERS;

  const handleToggle = (key: string, checked: boolean) => {
    setVisible((prev) => {
      const next = new Set(prev);
      checked ? next.add(key) : next.delete(key);
      return next;
    });
  };

  return (
    <div className="page-content">
      {maximized && (
        <MaximizedOverlay
          url={imageUrl}
          prInference={prInference}
          gtInference={gtInferencePr}
          assignment={assignment}
          visible={visible}
          runType={runType}
          pcHighlight={pcHighlight}
          layers={layers}
          onToggle={handleToggle}
          onClose={() => setMaximized(false)}
          onShapeRightClick={runType === 'PR' ? handleShapeRightClick : undefined}
        />
      )}

      {rightClick && runType === 'PR' && (
        <PrComparisonPanel
          state={rightClick}
          cameraImage={cameraImg}
          onClose={() => setRightClick(null)}
        />
      )}

      <div className="eval-page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Link
              to={`/eval/runs/${runId}`}
              style={{ fontSize: 12, color: 'var(--text-secondary)' }}
            >
              ← Run {runId}
            </Link>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              className="eval-page-title"
              style={{ fontFamily: 'var(--font-mono)', fontSize: 15 }}
            >
              {runInferenceId}
            </div>
            {run && <RunTypeBadge runType={run.run_type} />}
          </div>
          <div className="eval-page-sub">
            GT sample{' '}
            <Link
              to={`/eval/gt-samples/${detail.gt_sample.gt_sample_id}`}
              style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)', fontSize: 11 }}
            >
              {detail.gt_sample.gt_sample_id}
            </Link>
            {detail.gt_sample?.ingested_at && (
              <span style={{ marginLeft: 8 }}>· {fmtIso(detail.gt_sample.ingested_at)}</span>
            )}
          </div>
        </div>
        {siblingNav.idx >= 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              className="eval-btn-ghost"
              disabled={!siblingNav.prev}
              onClick={() => goToSibling(siblingNav.prev)}
              title="Previous sample in this run"
            >
              ← Prev
            </button>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                color: 'var(--text-secondary)',
                whiteSpace: 'nowrap',
              }}
            >
              {siblingNav.idx + 1} / {siblings.length}
            </span>
            <button
              className="eval-btn"
              disabled={!siblingNav.next}
              onClick={() => goToSibling(siblingNav.next)}
              title="Next sample in this run"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {detail.error && <div className="eval-reason-box">{detail.error}</div>}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(300px, 1fr) minmax(300px, 2fr)',
          gap: 20,
          marginBottom: 24,
          alignItems: 'start',
        }}
      >
        <div style={{ position: 'sticky', top: 16, alignSelf: 'start' }}>
          <ImageViewer
            url={imageUrl}
            prInference={prInference}
            gtInference={gtInferencePr}
            assignment={assignment}
            visible={visible}
            runType={runType}
            pcHighlight={pcHighlight}
            onMaximize={() => setMaximized(true)}
            onShapeRightClick={runType === 'PR' ? handleShapeRightClick : undefined}
          />
          {!isPcLike && (
            <LayerControls
              layers={layers}
              prInference={prInference}
              visible={visible}
              onChange={handleToggle}
            />
          )}
          {isPcLike && (
            <div
              style={{
                marginTop: 6,
                fontSize: 11,
                color: 'var(--text-tertiary)',
                textAlign: 'center',
              }}
            >
              Hover a ROG row to highlight polygons
            </div>
          )}
          {runType === 'PR' && (
            <div
              style={{
                marginTop: 6,
                fontSize: 11,
                color: 'var(--text-tertiary)',
                textAlign: 'center',
              }}
            >
              Hint: right-click on a polygon for details
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0 }}>
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                marginBottom: 12,
              }}
            >
              Sample Metrics
            </div>
            {detail.metrics ? (
              <MetricsSummary result={detail.metrics} />
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                No metrics — this sample failed.
              </div>
            )}
          </div>
          {isPcLike && rogRows.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  marginBottom: 6,
                  flexShrink: 0,
                }}
              >
                ROG Comparison by Position
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--text-tertiary)',
                  marginBottom: 8,
                  flexShrink: 0,
                }}
              >
                Hover a row or cell to highlight polygons.{' '}
                <span style={{ color: '#16a34a' }}>■</span> GT &nbsp;
                <span style={{ color: 'rgba(59,130,246,0.9)' }}>■</span> Run
              </div>
              <RogTable
                rows={rogRows}
                hoveredPosition={hoveredRogPosition}
                onHover={setHoveredRogPosition}
              />
            </div>
          )}
          {isPcLike && detail.gt_sample.pog_items && detail.gt_sample.pog_items.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  marginBottom: 6,
                }}
              >
                Planogram Grid
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--text-tertiary)',
                  marginBottom: 8,
                  display: 'flex',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <span>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      background: 'rgba(34,197,94,0.18)',
                      border: '1px solid rgba(34,197,94,0.55)',
                      borderRadius: 2,
                      marginRight: 4,
                    }}
                  />
                  GT = Run status
                </span>
                <span>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      background: 'rgba(239,68,68,0.18)',
                      border: '1px solid rgba(239,68,68,0.55)',
                      borderRadius: 2,
                      marginRight: 4,
                    }}
                  />
                  Mismatch
                </span>
                <span>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      background: 'rgba(251,146,60,0.18)',
                      border: '1px solid rgba(251,146,60,0.55)',
                      borderRadius: 2,
                      marginRight: 4,
                    }}
                  />
                  One side only
                </span>
              </div>
              <RogPlanogramGrid
                pogItems={detail.gt_sample.pog_items}
                gtPcInference={
                  (detail.gt_sample?.pc_inference ?? null) as Record<string, unknown> | null
                }
                runPcInference={detail.pc_inference as Record<string, unknown> | null}
                hoveredPosition={hoveredRogPosition}
                onHover={setHoveredRogPosition}
              />
            </div>
          )}
        </div>
      </div>

      {detail.gt_sample && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 12,
          }}
        >
          {detail.gt_sample.store_id != null && (
            <div className="eval-info-card">
              <div className="eval-info-label">Store</div>
              <div className="eval-info-value">{detail.gt_sample.store_id}</div>
            </div>
          )}
          {detail.gt_sample.chain_id != null && (
            <div className="eval-info-card">
              <div className="eval-info-label">Chain</div>
              <div className="eval-info-value">{detail.gt_sample.chain_id}</div>
            </div>
          )}
          {detail.gt_sample.image_id != null && (
            <div className="eval-info-card">
              <div className="eval-info-label">Image ID</div>
              <div className="eval-info-value" style={{ fontFamily: 'var(--font-mono)' }}>
                {detail.gt_sample.image_id}
              </div>
            </div>
          )}
          {detail.gt_sample.ingested_by && (
            <div className="eval-info-card">
              <div className="eval-info-label">Ingested By</div>
              <div className="eval-info-value">{detail.gt_sample.ingested_by}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
